// DateControl.tsx
import React, { useEffect, useState } from 'react';
import { Control } from '../ControlRenderer';
import { get, set } from '../utils/Utils'
import { TextField, InputAdornment, IconButton, Typography, FormControl, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { FormModeType } from '@/components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import { format, parse } from 'date-fns';

export interface DateControl extends Control {
  /**
   * A string used to render the current date value in readonly mode.
   */
  format?: string;

  /**
   * If true, the control will allow time selection in addition to date.
   */
  allowTime?: boolean;
}

interface DateControlProps {
  control: Control,
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const DateControl: React.FC<DateControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const handleFieldChange = (fieldName: string, value: string) => {
    if (value === '') {
      // Treat empty as null by default
      setSelectedValue(null);
      set(data, fieldName, null);
      onDataChange(data);
    }
    else {
      setSelectedValue(value);
      set(data, fieldName, value);
      onDataChange(data);
    }
    validateData();
  };

  const textStyle: React.CSSProperties = {
  };

  const [selectedValue, setSelectedValue] = useState<any>(get(data, control.dataKey ?? '', '') ?? '');

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;

  useEffect(() => {
    setSelectedValue(get(data, control.dataKey ?? '', '') ?? '')
  }, [data, control.dataKey]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  let currentMode = control.mode ? control.mode : mode;

  const dateControl = control as DateControl;
  const allowTime = dateControl.allowTime ?? false;

  const dateFormat = dateControl.format ?? 'MM/dd/yyyy';
  const displayValue = selectedValue ? format(selectedValue, dateFormat) : '';

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {displayValue}
      </Typography>
    )
  }

  // Conditionally render type="text" when in read-only mode and selectedValue is empty
  const inputType = currentMode === 'view'? 'text' : (allowTime? 'datetime-local' : 'date');

  if (currentMode === 'view') {
    textStyle.backgroundColor = '#ebebeb';
  }

  if (control.minWidth) {
    textStyle.minWidth = control.minWidth;
  }

  return (
    <>
      {errorMessage ? (
        <FormControl variant="standard" error><TextField
          type={inputType}
          variant="standard"
          className={`eform-date eform-text-${theme.palette.mode}`}
          value={currentMode === 'view'? displayValue: selectedValue}
          error
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string)}
          inputProps={{ readOnly: currentMode === 'view' }}
          style={textStyle}
        /><FormHelperText>{errorMessage}</FormHelperText></FormControl>
      ) : (
        <TextField
          type={inputType}
          variant="standard"
          className={`eform-date eform-text-${theme.palette.mode}`}
          value={currentMode === 'view'? displayValue: selectedValue}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string)}
          inputProps={{ readOnly: currentMode === 'view' }}
          style={textStyle}
        />
      )}
    </>
  );
};

export default DateControl;